import { useCallback, useEffect, useRef, useState } from 'react'
import { applyReportScope } from '../utils/reportScope'
import { prepareReportLotFilter } from '../utils/reportDeletedLots'
import { ensureFarmList } from '../utils/farmWizard'
import { lotApi } from '../utils/lotApi'
import { getDataset } from '../utils/reportDatasets'
import { localizeReportRows } from '../utils/reportLabels'
import type { ReportDefinitionV1 } from '../utils/reportStorage'
import { toMessage, type ReportRunState } from '../utils/panelWidgetRendering'
import { useT } from '../i18n'

/**
 * Fetches and caches report rows per report id, keyed the same way
 * NewDashboard.tsx's `runStates` always has — extracted so Dashboard.tsx's
 * pinned-widget preview shares the exact same fetch/cache behavior instead
 * of a second duplicate implementation. The effect only (re-)fetches a
 * report that isn't already loaded or currently loading, so passing the
 * same reports list across renders is cheap; `refetch` forces a fresh fetch
 * regardless (Panel's per-widget "Refresh" button and its "Edit" button,
 * which wants current rows for the draft preview even if already cached).
 */
// Findings #224: widgets leave out deleted lots' records as Reportes does,
// whichever screen opened first. A failed load leaves the rows as they were.
const prepareDeletedLotFilter = async (): Promise<void> => {
  const [farms, lots] = await Promise.all([ensureFarmList().catch(() => []), lotApi.list().catch(() => [])])
  await prepareReportLotFilter({
    farmIds: farms.map((farm) => String(farm.id ?? '')).filter(Boolean),
    lots
  }).catch(() => undefined)
}

export const usePanelWidgetReportRows = (
  reports: ReportDefinitionV1[]
): { runStates: Record<string, ReportRunState>; refetch: (report: ReportDefinitionV1) => void } => {
  const t = useT()
  const [runStates, setRunStates] = useState<Record<string, ReportRunState>>({})
  const runStatesRef = useRef(runStates)
  runStatesRef.current = runStates
  // Widgets of one Panel load share a single lookup.
  const filterLoadRef = useRef<Promise<void> | null>(null)

  const fetchOne = useCallback((report: ReportDefinitionV1) => {
    const dataset = getDataset(report.datasetKey)
    if (!dataset) {
      setRunStates((prev) => ({
        ...prev,
        [report.id]: {
          loading: false,
          error: t('dashboardNew.error.unknownDataset', { datasetKey: report.datasetKey }),
          rows: []
        }
      }))
      return
    }

    setRunStates((prev) => ({
      ...prev,
      [report.id]: { loading: true, error: null, rows: prev[report.id]?.rows ?? [] }
    }))

    filterLoadRef.current ??= prepareDeletedLotFilter().finally(() => {
      filterLoadRef.current = null
    })
    filterLoadRef.current
      .then(() => dataset.fetchRows(report.scope, report))
      .then((rows) => {
        setRunStates((prev) => ({ ...prev, [report.id]: { loading: false, error: null, rows: localizeReportRows(t, applyReportScope(rows, report.scope)) } }))
      })
      .catch((error: unknown) => {
        setRunStates((prev) => ({ ...prev, [report.id]: { loading: false, error: toMessage(error), rows: [] } }))
      })
  }, [t])

  useEffect(() => {
    reports.forEach((report) => {
      const existing = runStatesRef.current[report.id]
      if (existing?.loading) return
      if (existing && !existing.loading && existing.error === null) return
      fetchOne(report)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, fetchOne])

  return { runStates, refetch: fetchOne }
}
